import { api } from "../config/api";
import { post } from "../api";

export interface GroupsSessionArgs {
  business_id: string | null;
  device_token: string | null;
  session_id: string | null;
}

export interface CaseGroupRow {
  case_group_id?: string | number;
  name: string;
  shortcode: string;
  description: string;
  total_cases?: number;
  created_on?: string;
}

export interface ClientGroupRow {
  client_group_id?: string | number;
  name: string;
  shortcode: string;
  description: string;
  total_clients?: number;
  created_on?: string;
  is_active?: boolean;
}

export interface BusinessTeamMemberRow {
  user_id?: string | number;
  name?: string;
  first_name?: string;
  surname?: string;
  email?: string;
}

export interface CaseGroupDetailRow {
  case_group_id?: string | number;
  name: string;
  shortcode: string;
  description: string;
  created_by?: string;
  created_on?: string;
  assigned_owner?: string;
  assigned_owner_id?: string | number;
  total_cases?: number;
}

export interface CaseGroupEntryRow {
  case_id?: string | number;
  case_name?: string;
  client_name?: string;
  service?: string;
  case_status?: string;
  created_on?: string;
  added_by?: string;
  group_case_entry_id?: string | number;
}

export interface ClientGroupClientRow {
  group_id?: string | number;
  client_id?: string | number;
  user_id?: string | number | null;
  username?: string | null;
  full_name?: string;
  email?: string;
  group_name?: string;
  group_shortcode?: string;
  is_active?: boolean;
  client_type?: string;
  pan_id?: string;
  country_code?: string;
  phone?: string;
  status?: string;
  added_by?: string;
}

export interface GetCaseGroupsResponse {
  success?: boolean;
  data?: unknown;
  errors?: string;
}

export interface GetClientGroupsResponse {
  success?: boolean;
  data?: unknown;
  errors?: string;
}

export interface GroupsGetBusinessTeamResponse {
  success?: boolean;
  data?: { business_team?: unknown[] };
  errors?: string;
}

export interface GetCaseGroupDetailsArgs extends GroupsSessionArgs {
  case_group_id: string | number;
}

export interface GetCaseGroupDetailsResponse {
  success?: boolean;
  data?: unknown;
  errors?: string;
}

export interface GetCaseGroupEntriesArgs extends GroupsSessionArgs {
  case_group_id: string | number;
  page_number?: number;
  page_size?: number;
}

export interface GetCaseGroupEntriesResponse {
  success?: boolean;
  data?: unknown;
  errors?: string;
}

export interface GroupsGetCasesArgs extends GroupsSessionArgs {
  page_number?: number;
  page_size?: number;
}

export interface GroupsGetCasesResponse {
  success?: boolean;
  data?: { cases?: unknown[] };
  errors?: string;
}

export interface GetClientGroupClientsArgs extends GroupsSessionArgs {
  client_group_id: string | number;
}

export interface GetClientGroupClientsResponse {
  success?: boolean;
  data?: unknown;
  errors?: string;
}

export interface GroupsGetClientsResponse {
  success?: boolean;
  data?: unknown;
  errors?: string;
}

export interface CreateOrUpdateCaseGroupPayload extends GroupsSessionArgs {
  name: string;
  shortcode: string;
  description: string;
  case_group_id?: string | number;
}

export interface CreateOrUpdateCaseGroupResponse {
  success?: boolean;
  errors?: string;
}

export interface DeleteCaseGroupPayload extends GroupsSessionArgs {
  case_group_id: string | number;
}

export interface DeleteCaseGroupResponse {
  success?: boolean;
  errors?: string;
}

export interface CreateCaseGroupEntryPayload extends GroupsSessionArgs {
  case_group_id: string | number;
  case_ids: (string | number)[];
  is_active?: boolean;
}

export interface CreateCaseGroupEntryResponse {
  success?: boolean;
  errors?: string;
}

export interface DeleteCaseGroupEntryPayload extends GroupsSessionArgs {
  case_group_id: string | number;
  case_group_entry_id: (string | number)[];
}

export interface DeleteCaseGroupEntryResponse {
  success?: boolean;
  errors?: string;
}

export interface CreateOrUpdateClientGroupPayload extends GroupsSessionArgs {
  name: string;
  shortcode: string;
  description: string;
  is_active: boolean;
  client_group_id?: string | number;
}

export interface CreateOrUpdateClientGroupResponse {
  success?: boolean;
  errors?: string;
}

export interface UpdateGroupClientsPayload extends GroupsSessionArgs {
  client_group_id: string | number;
  client_id: (string | number)[];
  is_active: boolean;
}

export interface UpdateGroupClientsResponse {
  success?: boolean;
  count?: number;
  errors?: string;
}

function getCaseGroups(session: GroupsSessionArgs): Promise<GetCaseGroupsResponse> {
  return post<GetCaseGroupsResponse>(api.GET_ALL_CASE_GROUP, session);
}

function getClientGroups(session: GroupsSessionArgs): Promise<GetClientGroupsResponse> {
  return post<GetClientGroupsResponse>(api.GET_BUSINESS_CLIENT_GROUPS, session);
}

function getBusinessTeam(session: GroupsSessionArgs): Promise<GroupsGetBusinessTeamResponse> {
  return post<GroupsGetBusinessTeamResponse>(api.GET_BUSINESS_TEAM, session);
}

function getCaseGroupDetails(args: GetCaseGroupDetailsArgs): Promise<GetCaseGroupDetailsResponse> {
  return post<GetCaseGroupDetailsResponse>(api.GET_CASE_GROUP_DETAILS, args);
}

function getCaseGroupEntries(args: GetCaseGroupEntriesArgs): Promise<GetCaseGroupEntriesResponse> {
  return post<GetCaseGroupEntriesResponse>(api.GET_CASE_GROUP_ENTRY, {
    ...args,
    page_number: args.page_number ?? 1,
    page_size: args.page_size ?? 10,
  });
}

function getCases(args: GroupsGetCasesArgs): Promise<GroupsGetCasesResponse> {
  return post<GroupsGetCasesResponse>(api.GET_CASES_URL, {
    ...args,
    page_number: args.page_number ?? 1,
    page_size: args.page_size ?? 500,
  });
}

function getClientGroupClients(
  args: GetClientGroupClientsArgs
): Promise<GetClientGroupClientsResponse> {
  return post<GetClientGroupClientsResponse>(api.GET_GROUP_CLIENTS_NEW, args);
}

function getClients(args: GroupsGetCasesArgs): Promise<GroupsGetClientsResponse> {
  return post<GroupsGetClientsResponse>(api.GET_ALL_CLIENTS, {
    ...args,
    page_number: args.page_number ?? 1,
    page_size: args.page_size ?? 500,
  });
}

function createOrUpdateCaseGroup(
  payload: CreateOrUpdateCaseGroupPayload
): Promise<CreateOrUpdateCaseGroupResponse> {
  const url =
    payload.case_group_id !== null && payload.case_group_id !== undefined
      ? api.EDIT_CASE_GROUP
      : api.CREATE_CASE_GROUP;
  return post<CreateOrUpdateCaseGroupResponse>(url, payload);
}

function deleteCaseGroup(payload: DeleteCaseGroupPayload): Promise<DeleteCaseGroupResponse> {
  return post<DeleteCaseGroupResponse>(api.DELETE_CASE_GROUP, payload);
}

function createCaseGroupEntry(
  payload: CreateCaseGroupEntryPayload
): Promise<CreateCaseGroupEntryResponse> {
  return post<CreateCaseGroupEntryResponse>(api.CREATE_CASE_GROUP_ENTRY, {
    ...payload,
    case_id: payload.case_ids,
    is_active: payload.is_active ?? true,
  });
}

function deleteCaseGroupEntry(
  payload: DeleteCaseGroupEntryPayload
): Promise<DeleteCaseGroupEntryResponse> {
  const { business_id, device_token, session_id, case_group_entry_id } = payload;
  return post<DeleteCaseGroupEntryResponse>(api.DELETE_CASE_GROUP_ENTRY, {
    business_id,
    device_token,
    session_id,
    case_group_entry_id,
  });
}

function createOrUpdateClientGroup(
  payload: CreateOrUpdateClientGroupPayload
): Promise<CreateOrUpdateClientGroupResponse> {
  return post<CreateOrUpdateClientGroupResponse>(api.CREATE_CLIENT_GROUP, payload);
}

function updateGroupClients(
  payload: UpdateGroupClientsPayload
): Promise<UpdateGroupClientsResponse> {
  return post<UpdateGroupClientsResponse>(api.UPDATE_GROUP_CLIENTS_NEW, payload);
}

export const groupsService = {
  getCaseGroups,
  getClientGroups,
  getBusinessTeam,
  getCaseGroupDetails,
  getCaseGroupEntries,
  getCases,
  getClientGroupClients,
  getClients,
  createOrUpdateCaseGroup,
  deleteCaseGroup,
  createCaseGroupEntry,
  deleteCaseGroupEntry,
  createOrUpdateClientGroup,
  updateGroupClients,
};
